import React, { useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import editorUiStyles from './styles';
import { injectEditing } from './utils/iframe';
import { useEditorCommands } from './hooks/useEditorCommands';
import { usePageManager } from './hooks/usePageManager';
import { useIframeUndoHistory } from './hooks/useIframeUndoHistory';
import { useTableEditor } from './hooks/useTableEditor';
import { Toolbar } from './components/Toolbar';
import { usePrompt } from '../AppPrompt';

export interface HtmlReportEditorHandle {
  getEditedHtml: () => string;
  isDirty: () => boolean;
}

interface HtmlReportEditorProps {
  htmlContent: string;
}

/**
 * A rich-text editor that renders a report's HTML inside a sandboxed iframe
 * with `document.designMode = 'on'`. Supports multi-page reports, live footer
 * editing, table row operations, and full text formatting.
 *
 * Exposes `{ getEditedHtml }` via `ref` so the parent can read back the
 * edited HTML when the user saves.
 */
const HtmlReportEditor = forwardRef<HtmlReportEditorHandle, HtmlReportEditorProps>(function HtmlReportEditor({ htmlContent }, ref) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { getConfirmation } = usePrompt();

  const {
    exec,
    syncState,
    saveSelection,
    restoreSelection,
    applyFontSizeFromToolbar,
    fmt,
    textColor,
    setTextColorState,
    hlColor,
    setHlColorState,
    fontFamily,
    fontSize,
    cellBgColor,
    setCellBgColor,
    inTable,
    setInTable,
    activeCellRef,
  } = useEditorCommands(iframeRef);

  const iframeHistory = useIframeUndoHistory(iframeRef, htmlContent, syncState);

  const { handleLoad, insertPageBreak, deleteCurrentPage, editorPageCount, reportLayoutPending } = usePageManager(iframeRef, {
    reportContentKey: htmlContent,
    syncState,
    getConfirmation,
  });

  const { insertRowAbove, insertRowBelow, deleteRow, applyCellBg } = useTableEditor(
    iframeRef,
    activeCellRef,
    setCellBgColor,
    setInTable,
    syncState,
    iframeHistory,
  );

  const execForToolbar = useCallback(
    (cmd: string, value: string | null = null) => {
      if (cmd === 'undo') {
        if (iframeHistory.tryUndo()) return;
        exec('undo');
        iframeHistory.syncBaselineFromDom();
        return;
      }
      if (cmd === 'redo') {
        if (iframeHistory.tryRedo()) return;
        exec('redo');
        iframeHistory.syncBaselineFromDom();
        return;
      }
      if (cmd === 'underline') {
        iframeHistory.recordBeforeMutate();
      }
      exec(cmd, value);
    },
    [exec, iframeHistory],
  );

  const getEditedHtml = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return '';

    const clone = doc.documentElement.cloneNode(true) as HTMLElement;

    // Remove the <style> and <script> the editor injected so the backend PDF
    // renderer doesn't see overflow:hidden or our screen-only layout rules.
    clone.querySelectorAll('[data-editor-inject]').forEach(el => el.remove());

    // Remove page-UI chrome — the PDF renderer doesn't honour @media print.
    clone.querySelectorAll('.__pg-badge__, .__pg-sep__, .__pg-footer__').forEach(el => el.remove());

    // The bootstrap script set overflow:hidden / height:auto as inline styles on
    // <html> and <body>.  Those survive serialisation and cause the PDF renderer
    // to clip wide tables on the right.  Strip them so page dimensions come only
    // from the report's own stylesheet.
    for (const prop of ['overflow', 'min-height', 'height']) {
      clone.style.removeProperty(prop);
    }
    const body = clone.querySelector('body');
    if (body) {
      for (const prop of ['overflow', 'min-height', 'height']) {
        body.style.removeProperty(prop);
      }
    }

    // Remove contentEditable attributes the editor placed on page-sep buttons.
    clone.querySelectorAll('[contenteditable]').forEach(el => el.removeAttribute('contenteditable'));

    return clone.outerHTML;
  }, []);

  useImperativeHandle(ref, () => ({ getEditedHtml, isDirty: iframeHistory.isDirty }), [getEditedHtml, iframeHistory.isDirty]);

  return (
    <div className='hre-root flex flex-col h-full' style={{ background: '#f8f9fa', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <style>{editorUiStyles}</style>

      <Toolbar
        exec={execForToolbar}
        fmt={fmt}
        saveSelection={saveSelection}
        restoreSelection={restoreSelection}
        applyFontSizeFromToolbar={applyFontSizeFromToolbar}
        textColor={textColor}
        setTextColorState={setTextColorState}
        hlColor={hlColor}
        setHlColorState={setHlColorState}
        fontFamily={fontFamily}
        fontSize={fontSize}
        inTable={inTable}
        cellBgColor={cellBgColor}
        applyCellBg={applyCellBg}
        insertPageBreak={insertPageBreak}
        deleteCurrentPage={deleteCurrentPage}
        editorPageCount={editorPageCount}
        insertRowAbove={insertRowAbove}
        insertRowBelow={insertRowBelow}
        deleteRow={deleteRow}
      />

      <div className='flex-1 overflow-auto py-4 px-4' style={{ background: '#f0f0f0', minHeight: 0 }}>
        <div className='mx-auto relative' style={{ width: '100%', background: '#f0f0f0' }}>
          {reportLayoutPending && (
            <Box
              aria-busy='true'
              aria-live='polite'
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                bgcolor: 'rgba(255,255,255,0.92)',
                pointerEvents: 'auto',
              }}
            >
              <CircularProgress />
              <Typography variant='body2' color='text.secondary'>
                Loading report…
              </Typography>
            </Box>
          )}
          <iframe
            ref={iframeRef}
            srcDoc={injectEditing(htmlContent)}
            onLoad={handleLoad}
            title='HTML Report Editor'
            style={{
              width: '100%',
              minHeight: '900px',
              border: 'none',
              display: 'block',
              pointerEvents: reportLayoutPending ? 'none' : 'auto',
            }}
          />
        </div>
      </div>
    </div>
  );
});

export default HtmlReportEditor;
